import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type SandboxAction = {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
  inputSchema: {
    type: 'object';
    properties: Record<
      string,
      {
        type: string;
        description?: string;
        sensitive?: boolean;
        enum?: string[];
        default?: unknown;
        minLength?: number;
      }
    >;
    required?: string[];
  };
  outputSchema: {
    type: 'object';
    properties: Record<string, { type: string; description?: string }>;
  } | null;
  credentialOutputConfig: {
    fieldMapping: Record<string, string>;
    loginUrl?: string;
    loginInstructions?: string;
    provisioningInstructions?: string;
    /** Persona variable name → literal or `$output.*` token; fills `$var.USER.<name>`. */
    variableMapping?: Record<string, string>;
  } | null;
  metadata: Record<string, unknown> | null;
  timeoutMs: number;
  createdAt: string;
  updatedAt: string;
};

export type ActionExecution = {
  id: string;
  actionId: string;
  instanceId: string;
  source: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timeout';
  result: Record<string, unknown> | null;
  stdout: string | null;
  stderr: string | null;
  exitCode: number | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type ActionExecuteResult = {
  executionId: string;
  status: string;
  result: Record<string, unknown> | null;
  stdout: string | null;
  stderr: string | null;
  exitCode: number | null;
  durationMs: number | null;
  error: string | null;
};

/** Fetch available actions for an instance (from its template). */
export function useSandboxActions(instanceId: string | undefined) {
  return useQuery({
    queryKey: ['sandbox-actions', instanceId],
    queryFn: async (): Promise<SandboxAction[]> => {
      const res = await fetch(`${API_URL}/org/environments/instances/${instanceId}/actions`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load actions');
      const json = (await res.json()) as { ok: boolean; data: SandboxAction[] };
      return json.data;
    },
    enabled: !!instanceId,
  });
}

/** Fetch execution history for an instance. */
export function useSandboxActionExecutions(instanceId: string | undefined) {
  return useQuery({
    queryKey: ['sandbox-action-executions', instanceId],
    queryFn: async (): Promise<ActionExecution[]> => {
      const res = await fetch(
        `${API_URL}/org/environments/instances/${instanceId}/actions/executions`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load executions');
      const json = (await res.json()) as { ok: boolean; data: ActionExecution[]; nextCursor: string | null };
      return json.data;
    },
    enabled: !!instanceId,
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.some(
        (e) => e.status === 'pending' || e.status === 'running'
      );
      return hasRunning ? 3000 : false;
    },
  });
}

/** Execute an action against an instance. */
export function useExecuteSandboxAction(instanceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slug,
      params,
    }: {
      slug: string;
      params: Record<string, unknown>;
    }): Promise<ActionExecuteResult> => {
      const res = await fetch(
        `${API_URL}/org/environments/instances/${instanceId}/actions/${slug}/execute`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ params, source: 'ui' }),
        }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to execute action');
      const json = (await res.json()) as { ok: boolean; data: ActionExecuteResult };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-action-executions', instanceId] });
    },
  });
}
